// @flow
import styled from '@emotion/styled';
import { SPACING } from '@tablekit/theme';
import Button from '@tablekit/button';
import Icon from '@tablekit/icon';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import type {
  BlogAggregateCategoryType,
  BlogPostType,
  BlokComponentPropsType
} from 'components/types';
import Link from 'components/Link';
import Title from 'styled/Title';
import {
  DESKTOP_BREAKPOINT,
  LAPTOP_MAX_WIDTH,
  MOBILE_BREAKPOINT,
  MOBILE_MAX_WIDTH
} from 'utils/theme';
import { Grid, GUTTER, CARD_FORMATS } from './styled';
import BlogPreview from './Preview';
import columnWidth from '../../../utils/columnWidth';

type PropsType = BlokComponentPropsType<
  BlogAggregateCategoryType & {
    posts: BlogPostType[]
  }
> & {
  align?: 'left' | 'center',
  shouldShowSeeMoreArrow?: boolean
};

const AggregateCategoryWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) => (align === 'left' ? 'stretch' : 'center')};
  position: relative;
  max-width: ${columnWidth(12)}%;

  @media ${DESKTOP_BREAKPOINT} {
    max-width: ${columnWidth(10)}%;
  }
  @media (min-width: ${MOBILE_MAX_WIDTH}) and (max-width: ${LAPTOP_MAX_WIDTH}) {
    max-width: ${columnWidth(10)}%;
  }

  @media ${MOBILE_BREAKPOINT} {
    align-items: stretch;
    margin: 0;
  }
`;

export const SeeMoreWrapper = styled.div`
  -ms-grid-column: 3;
  -ms-grid-column-span: 4;
  grid-column-start: 3;
  grid-column-end: 7;
  -ms-grid-row: 2;
  grid-row-start: 2;

  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  margin: ${parseInt(SPACING.L8, 10) - GUTTER / 2}px ${GUTTER / 2}px
    ${parseInt(SPACING.L8, 10) - GUTTER / 2}px;
  @media ${MOBILE_BREAKPOINT} {
    margin: 0 0 ${SPACING.L4};
  }
`;

const CategoryTitle = styled(Title)`
  @media ${MOBILE_BREAKPOINT} {
    margin: 0 ${SPACING.L4} ${SPACING.L4};
  }
`;

const BlogAggregateCategory = ({
  blok: {
    posts = [],
    title,
    see_more_link: seeMoreLink,
    see_more_link_text: seeMoreText,
    max_count: maxCount,
    show_author: showAuthor,
    show_categories: showCategories,
    show_preview_text: showPreviewText,
    show_published_date: showPublishedDate,
    show_read_time: showReadTime
  },
  align = 'center',
  shouldShowSeeMoreArrow = false
}: PropsType) => {
  if (posts.length === 0) {
    return null;
  }
  let format = CARD_FORMATS.THIRD;
  let msColumnSpan = '2';
  if (maxCount === '1') {
    format = CARD_FORMATS.FULL;
    msColumnSpan = '6';
  } else if (maxCount === '2') {
    format = CARD_FORMATS.HALF;
    msColumnSpan = '3';
  }
  let seeMore = null;
  if (seeMoreLink && seeMoreText) {
    seeMore = (
      <SeeMoreWrapper>
        <Button
          component={Link}
          size={{ [MOBILE_BREAKPOINT]: 'small', default: 'large' }}
          appearance="subtle"
          link={seeMoreLink}
          iconAfter={
            shouldShowSeeMoreArrow ? <Icon icon={faArrowRight} /> : undefined
          }
        >
          {seeMoreText}
        </Button>
      </SeeMoreWrapper>
    );
  }
  return (
    <AggregateCategoryWrapper align={align}>
      <CategoryTitle align={align}>{title}</CategoryTitle>
      <Grid>
        {posts.map(
          (
            {
              uuid,
              full_slug: fullSlug,
              first_published_at: publishedDate,
              content: {
                author,
                categories: postCategories,
                hero_title: blogTitle,
                preview_image: image,
                preview_text: text,
                read_time: readTime
              }
            },
            index
          ) => (
            <BlogPreview
              showAuthor={showAuthor}
              showCategories={showCategories}
              showPublishedDate={showPublishedDate}
              showReadTime={showReadTime}
              showPreviewText={showPreviewText}
              key={uuid}
              image={image}
              slug={fullSlug}
              title={blogTitle}
              author={author}
              categories={postCategories}
              format={format}
              publishedDate={publishedDate}
              readTime={readTime}
              text={text}
              style={{
                msGridColumnSpan: msColumnSpan,
                msGridColumn: `${index * 2 + 1}`
              }}
            />
          )
        )}
        {seeMore}
      </Grid>
    </AggregateCategoryWrapper>
  );
};

export default BlogAggregateCategory;
